import type {
  ITaskQueueRepository,
  QueuedMutation,
} from '@domain/data-contracts/task-queue-repository.interface';

const DB_NAME = 'dentalcadlink-employee';
const DB_VERSION = 1;
const STORE = 'task-queue';

/**
 * IndexedDB-backed implementation of {@link ITaskQueueRepository}.
 * Used by the employee portal to persist mutations that failed
 * because the device went offline. Each entry is keyed by its
 * own `id`, which the caller (the Zustand store) generates with
 * `crypto.randomUUID()` so replays are idempotent.
 *
 * The class falls back to an in-memory map when `indexedDB` is
 * not available (e.g. SSR, sandboxed iframes, JSDOM without a
 * polyfill), which keeps unit tests cheap to run without an
 * IndexedDB shim.
 */
export class IndexedDbTaskQueueRepository implements ITaskQueueRepository {
  private readonly memoryFallback = new Map<string, QueuedMutation>();
  private dbPromise: Promise<IDBDatabase> | null = null;

  public constructor(private readonly factory: IDBFactory | undefined = readFactory()) {}

  public async enqueue(mutation: QueuedMutation): Promise<void> {
    const db = await this.openDb();
    if (!db) {
      this.memoryFallback.set(mutation.id, mutation);
      return;
    }
    await runRequest((store) => store.put(mutation), db, 'readwrite');
  }

  public async list(): Promise<readonly QueuedMutation[]> {
    const db = await this.openDb();
    if (!db) {
      return Array.from(this.memoryFallback.values()).sort(
        (a, b) => a.enqueuedAt - b.enqueuedAt,
      );
    }
    const all = await runRequest<readonly QueuedMutation[]>(
      (store) => store.getAll() as unknown as IDBRequest<readonly QueuedMutation[]>,
      db,
      'readonly',
    );
    return [...all].sort((a, b) => a.enqueuedAt - b.enqueuedAt);
  }

  public async remove(id: string): Promise<void> {
    const db = await this.openDb();
    if (!db) {
      this.memoryFallback.delete(id);
      return;
    }
    await runRequest((store) => store.delete(id), db, 'readwrite');
  }

  public async clear(): Promise<void> {
    const db = await this.openDb();
    if (!db) {
      this.memoryFallback.clear();
      return;
    }
    await runRequest((store) => store.clear(), db, 'readwrite');
  }

  private openDb(): Promise<IDBDatabase | null> {
    if (!this.factory) {
      return Promise.resolve(null);
    }
    if (!this.dbPromise) {
      this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const req = (this.factory as IDBFactory).open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (): void => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE)) {
            db.createObjectStore(STORE, { keyPath: 'id' });
          }
        };
        req.onsuccess = (): void => {
          resolve(req.result);
        };
        req.onerror = (): void => {
          reject(req.error ?? new Error('IndexedDB open failed'));
        };
      }).catch(() => {
        // Permanent fallback to in-memory queue.
        this.dbPromise = Promise.resolve(null as unknown as IDBDatabase);
        return null as unknown as IDBDatabase;
      });
    }
    return this.dbPromise;
  }
}

function readFactory(): IDBFactory | undefined {
  if (typeof globalThis === 'undefined') {
    return undefined;
  }
  const candidate = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
  return candidate;
}

function runRequest<T>(
  build: (store: IDBObjectStore) => IDBRequest<T> | IDBRequest<undefined>,
  db: IDBDatabase,
  mode: IDBTransactionMode,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const req = build(store);
    req.onsuccess = (): void => {
      resolve(req.result as T);
    };
    req.onerror = (): void => {
      reject(req.error ?? new Error('IndexedDB request failed'));
    };
  });
}
